/**
 * PII Scrubber - Issue #2347
 *
 * Sentry/PostHog送信前に個人情報フィールドを除去するユーティリティ。
 * APPI（個人情報保護法）準拠のため、外部サービスへのPII流出を防止。
 *
 * NOTE: このファイルは backend/src/common/utils/pii-scrubber.ts と同期必須。
 * 変更時は必ず両方を更新すること。
 */

export const PII_FIELDS = [
  // 個人情報
  'email',
  'customer_email',
  'phone',
  'customer_phone',
  'phoneNumber',
  'telephone',
  'firstName',
  'lastName',
  'name',
  'customer_name',
  'fullName',
  'address',
  'postalCode',
  'lineUserId',
  // セキュリティセンシティブ
  'password',
  'token',
  'secret',
  'apiKey',
  'creditCard',
  'cardNumber',
  'cvv',
  'ssn',
] as const;

const PII_FIELDS_SET = new Set<string>(PII_FIELDS);

/**
 * オブジェクトからPIIフィールドを [REDACTED] に置換する（再帰スクラブ）。
 * システムID（userId, tenantId等）は残す。
 * ネストされたオブジェクト内のPIIフィールドも再帰的に除去する。
 */
export function scrubPiiFields<T extends Record<string, unknown>>(
  obj: T,
  maxDepth = 5
): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const scrubbed = { ...obj };
  for (const key of Object.keys(scrubbed)) {
    const value = scrubbed[key];
    if (PII_FIELDS_SET.has(key) && value != null) {
      (scrubbed as Record<string, unknown>)[key] = '[REDACTED]';
    } else if (
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      maxDepth > 0
    ) {
      (scrubbed as Record<string, unknown>)[key] = scrubPiiFields(
        value as Record<string, unknown>,
        maxDepth - 1
      );
    }
  }
  return scrubbed;
}

/**
 * URLクエリパラメータからPIIを除去する。
 */
export function scrubUrlParams(url: string): string {
  if (!url || !url.includes('?')) {
    return url;
  }

  try {
    const [base, query] = url.split('?', 2);
    const params = new URLSearchParams(query);
    let modified = false;

    for (const key of Array.from(params.keys())) {
      if (PII_FIELDS_SET.has(key)) {
        params.set(key, '[REDACTED]');
        modified = true;
      }
    }

    return modified ? `${base}?${params.toString()}` : url;
  } catch (e) {
    console.warn('[PII-Scrubber] scrubUrlParams failed:', e);
    return url;
  }
}
